import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IGift } from '@app/gifts/models/gift.interface';
import { GiftsService } from '@app/gifts/services/gifts.service';
import { IVisitDetails } from '@app/shared/models/visit-details.model';
import { TruncatePipe } from '@app/shared/pipes/truncate-long-string.pipe';
import { SEOService, ISeoTagType, CountdownService } from '@app/shared/services';
import { catchError, finalize, switchMap, tap, iif, Observable, of } from 'rxjs';

@Component({
  selector: 'app-gift-details',
  templateUrl: './gift-details.component.html',
  styleUrls: ['./gift-details.component.scss'],
  providers: [TruncatePipe]
})
export class GiftDetailsComponent implements OnInit {
  giftDetails: IGift;
  visitDetails: IVisitDetails;

  giftChosen: boolean;
  giftReservationError: boolean;

  countdownRedirectInSeconds = 10;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private giftsService: GiftsService,
    private seoService: SEOService,
    private truncatePipe: TruncatePipe,
    private countdown: CountdownService
  ) { }

  ngOnInit(): void {
    this.giftDetails = this.route.snapshot.data.giftDetails;
    this.visitDetails = this.route.snapshot.data.visitDetails;
    this.setSEO();
  }

  goBack(): void {
    this.router.navigate([
      'gifts',
      'list',
      this.visitDetails.visitId
    ]);
  }

  bookAnotherVisit(): void {
    if (this.visitDetails.wasRoutedBySalonId) {
      this.router.navigate(['salon', this.visitDetails.salonId]);
      return;
    }
    this.router.navigate(['salon', this.visitDetails.salonCity, this.visitDetails.salonNameAlias]);
  }

  chooseGift(): void {
    this.giftsService.chooseGift(this.visitDetails.visitId, this.giftDetails.giftId)
      .pipe(
        switchMap(reservationSuccess => {
          this.giftChosen = true;
          this.giftReservationError = false;

          return iif(
            () => this.visitDetails.canRedirectToSalonWebpageOnRouteBySalonId,
            this.countdownToRedirect$(),
            of(reservationSuccess)
          );
        }),
        catchError(() => {
          this.giftReservationError = true;
          return of(null);
        }),
        finalize(() => {
          this.giftsService.clearCachedGifts();
        })
      )
      .subscribe();
  }

  returnToSalonWebsite(): void {
    window.location.replace(this.visitDetails.bookingReturnUrl);
  }

  private countdownToRedirect$(): Observable<number> {
    return this.countdown.getSecondsCountdown$(this.countdownRedirectInSeconds)
      .pipe(
        tap((remainingSeconds: number) => this.countdownRedirectInSeconds = remainingSeconds),
        finalize(() => this.returnToSalonWebsite())
      );
  }

  private setSEO(): void {
    const giftName = this.giftDetails
      ? this.truncatePipe.transform(this.giftDetails.giftName, [25, '(...)'])
      : '';

    this.seoService.setSEO([
      {
        tagType: ISeoTagType.Title,
        i18nKey: 'GIFTS.DETAILS-VIEW.META-TITLE',
        i18nParams: { giftName }
      },
      {
        tagType: ISeoTagType.Description,
        i18nKey: 'GIFTS.DETAILS-VIEW.META-DESCRIPTION'
      }
    ]);
  }

}
